import { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { useTranslate } from "src/locales";
import { useBaseQuery } from "src/api/core/useBaseQuery";
import { axiosInstance } from "src/api/config/axiosInstance";
import { NurseCarePlanResponse, TreeData, DeleteState, SecondGridDeleteState } from "../models";

export const usePatientCare = () => {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();

  // State Management
  const [date, setDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [refreshKey, setRefreshKey] = useState(0);
  const [mainRows, setMainRows] = useState<any[]>([]);
  const [selectedChildDetails, setSelectedChildDetails] = useState<any[]>([]);
  const [selectedPlanPeriods, setSelectedPlanPeriods] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [lastSelectedTreeId, setLastSelectedTreeId] = useState<number | null>(null);
  const [lastSelectedInterventionId, setLastSelectedInterventionId] = useState<number | null>(null);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSecondGridDeleting, setIsSecondGridDeleting] = useState(false);
  const [copiedGuideData, setCopiedGuideData] = useState<any>(null);

  // Dialog States
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditDialogOpen, setEditDialogOpen] = useState(false);
  const [currentEditRow, setCurrentEditRow] = useState<any>(null);
  const [isNurseEditDialogOpen, setNurseEditDialogOpen] = useState(false);
  const [nurseCurrentEditRow, setNurseCurrentEditRow] = useState<any>(null);
  const [isPlanEditDialogOpen, setPlanEditDialogOpen] = useState(false);
  const [planCurrentEditRow, setPlanCurrentEditRow] = useState<any>(null);
  const [dialogTreeOpen, setDialogTreeOpen] = useState(false);
  const [dialogTreeNewOpen, setDialogTreeNewOpen] = useState(false);
  const [selectedTreeId, setSelectedTreeId] = useState<number | null>(null);
  const [selectedRowForPeriodPlanning, setSelectedRowForPeriodPlanning] = useState<number | null>(null);
  const [selectedRowDataForNewPeriod, setSelectedRowDataForNewPeriod] = useState<any>(null);

  const [deleteState, setDeleteState] = useState<DeleteState>({
    open: false,
    rowId: null,
    isGuide: false,
    planIds: [],
  });

  const [secondGridDeleteState, setSecondGridDeleteState] = useState<SecondGridDeleteState>({
    open: false,
    rowId: null,
    deleteType: '',
  });

  // Redux Data
  const mainTabs = useSelector((state: any) => state.tabs.mainTabs);
  const activeTabIndex = useSelector((state: any) => state.tabs.activeTabIndex);
  const activeTab = mainTabs[activeTabIndex];
  const patientId = activeTab?.data?.patientId;

  // Data Fetching
  const { data: nurseCarePlanData, isLoading: fetchLoading, isFetching: fetchFetching } = useBaseQuery<NurseCarePlanResponse>({
    endpoint: `/nurse/api/PatientNurseCarePlan/GetPatientNurseCarePlan/${patientId}?date=${date}`,
    queryKey: ["PatientNurseCarePlan", patientId, date, refreshKey],
    options: {
      refetchOnWindowFocus: false,
      enabled: !!patientId,
    },
  });

  useEffect(() => {
    setIsDataLoading(fetchLoading || fetchFetching);
  }, [fetchLoading, fetchFetching]);

  useEffect(() => {
    if (nurseCarePlanData?.data) {
      setMainRows(nurseCarePlanData.data);
    }
  }, [nurseCarePlanData]);

  useEffect(() => {
    setRefreshKey(prev => prev + 1);
    setSelectedChildDetails([]);
    setSelectedPlanPeriods([]);
    setSelectedIds([]);
  }, [date, patientId]);

  // Tree Building Logic
  const buildTreeRows = useCallback((rawData: any[]): TreeData[] => {
    const groupMap = new Map<string, number>();
    let groupId = 1000000;
    const parentRows: TreeData[] = [];
    const childRowsByParent: Record<number, TreeData[]> = {};

    rawData.forEach((item, idx) => {
      const gName = item.ncg_name?.trim() || "";
      if (!groupMap.has(gName)) {
        const parentId = groupId++;
        groupMap.set(gName, parentId);
        parentRows.push({
          id: parentId,
          parentId: null,
          name: gName,
          no: "",
          target: "",
          plan_time: "",
          cuser: "",
          note: "",
          is_reached: "",
          is_not_reached: "",
        });
        childRowsByParent[parentId] = [];
      }
      const parentId = groupMap.get(gName)!;
      childRowsByParent[parentId].push({
        ...item,
        id: item.id,
        parentId: parentId,
        name: item.ncp_name?.trim() || "",
        no: idx + 1,
      });
    });

    const treeRows: TreeData[] = [];
    parentRows.forEach(parent => {
      treeRows.push(parent, ...childRowsByParent[parent.id]);
    });
    return treeRows;
  }, []);

  const treeRows = useMemo(() => buildTreeRows(mainRows), [mainRows, buildTreeRows]);

  // Event Handlers
  const handleDateChange = (day: 'previous' | 'next' | 'today') => {
    setDate(prev => {
      if (day === 'previous') {
        const d = new Date(prev);
        d.setDate(d.getDate() - 1);
        return d.toISOString().slice(0, 10);
      }
      if (day === 'next') {
        const d = new Date(prev);
        d.setDate(d.getDate() + 1);
        return d.toISOString().slice(0, 10);
      }
      return new Date().toISOString().slice(0, 10);
    });
  };

  const handleTreeSelect = async (id: number) => {
    setSelectedTreeId(id);
    if (id === 1) return; // Parent node "Selected Intervention Plan"

    if (id === 2) { // According to Defined Period
      if (selectedRowForPeriodPlanning) {
        await handleAddPeriodPlanning(selectedRowForPeriodPlanning);
      } else {
        enqueueSnackbar(t("patient-care.no-row-selected"), { variant: 'error' });
      }
    } else if (id === 3) { // New Period Plan
      if (selectedRowDataForNewPeriod) {
        setDialogTreeNewOpen(true);
      } else {
        enqueueSnackbar(t("patient-care.no-row-data-selected"), { variant: 'error' });
      }
    }
  };

  const handleAddPeriodPlanning = async (rowId: number) => {
    try {
      const selectedRow = selectedChildDetails.find(row => row.id === rowId);
      if (!selectedRow) {
        enqueueSnackbar(t("patient-care.row-not-found"), { variant: 'error' });
        return;
      }

      const requestData = {
        PncpId: rowId,
        Date: date,
        Period: selectedRow.period || 1
      };

      const response = await axiosInstance.post('nurse/api/PatientNurseCarePlan/AddPatientNurseCarePlanDetailPeriods', requestData);

      if (response.status === 200) {
        enqueueSnackbar(t("patient-care.period-planning-added-successfully"), { variant: 'success' });
        setRefreshKey(prev => prev + 1);
        setSelectedChildDetails([...selectedChildDetails]);
      } else {
        enqueueSnackbar(t("patient-care.period-planning-add-failed"), { variant: 'error' });
      }
    } catch (error: any) {
      console.error(error);
      enqueueSnackbar(t("patient-care.period-planning-add-error"), { variant: 'error' });
    }
  };

  const handleDeleteGuide = async (guideId: number) => {
    const planIds = treeRows.filter(row => row.parentId === guideId).map(row => row.id);
    if (planIds.length === 0) return;

    setDeleteState({
      open: true,
      rowId: guideId,
      isGuide: true,
      planIds: planIds,
    });
  };

  const handleDeletePlan = async (planId: number) => {
    setDeleteState({
      open: true,
      rowId: planId,
      isGuide: false,
      planIds: [],
    });
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      let endpoint = '';
      if (deleteState.isGuide) {
        endpoint = `nurse/api/PatientNurseCarePlan/DeletePatientNurseCarePlansByIds/${deleteState.planIds.join(',')}`;
      } else {
        endpoint = `nurse/api/PatientNurseCarePlan/DeletePatientNurseCarePlan/${deleteState.rowId}`;
      }

      const response = await axiosInstance.delete(endpoint);
      if (response.status === 200) {
        enqueueSnackbar(deleteState.isGuide ? t("patient-care.guide-deleted-successfully") : t("patient-care.plan-deleted-successfully"), { variant: 'success' });
        setDeleteState(prev => ({ ...prev, open: false }));
        setSelectedChildDetails([]);
        setSelectedPlanPeriods([]);
        setSelectedIds([]);
        setRefreshKey(prev => prev + 1);
      } else {
        enqueueSnackbar(deleteState.isGuide ? t("patient-care.guide-delete-failed") : t("patient-care.plan-delete-failed"), { variant: 'error' });
      }
    } catch (error: any) {
      console.error(error);
      enqueueSnackbar(t("patient-care.delete-error"), { variant: 'error' });
    } finally {
      setIsDeleting(true);
      setTimeout(() => setIsDeleting(false), 500); // UI feel optimization
    }
  };

  const handleDeleteIntervention = (rowId: number) => {
    setSecondGridDeleteState({
      open: true,
      rowId: rowId,
      deleteType: 'intervention',
    });
  };

  const handleDeletePlanning = (rowId: number) => {
    setSecondGridDeleteState({
      open: true,
      rowId: rowId,
      deleteType: 'planning',
    });
  };

  const handleSecondGridDeleteConfirm = async () => {
    setIsSecondGridDeleting(true);
    try {
      let endpoint = '';
      if (secondGridDeleteState.deleteType === 'intervention') {
        endpoint = `nurse/api/PatientNurseCarePlan/DeletePatientNurseCarePlanDetail/${secondGridDeleteState.rowId}`;
      } else if (secondGridDeleteState.deleteType === 'planning') {
        endpoint = `nurse/api/PatientNurseCarePlan/DeletePatientNurseCarePlanDetailPeriodByDetailId/${secondGridDeleteState.rowId}`;
      }

      const response = await axiosInstance.delete(endpoint);
      if (response.status === 200) {
        enqueueSnackbar(t("patient-care.delete-success"), { variant: 'success' });
        setSecondGridDeleteState(prev => ({ ...prev, open: false }));
        setSelectedChildDetails([]);
        setSelectedPlanPeriods([]);
        setSelectedIds([]);
        setRefreshKey(prev => prev + 1);
      } else {
        enqueueSnackbar(t("patient-care.delete-failed"), { variant: 'error' });
      }
    } catch (error: any) {
      console.error(error);
      enqueueSnackbar(t("patient-care.delete-error"), { variant: 'error' });
    } finally {
      setIsSecondGridDeleting(false);
    }
  };

  const handleCopyGuide = (guideId: number) => {
    const guide = treeRows.find(row => row.id === guideId && row.parentId === null);
    if (!guide) return;

    const plans = treeRows.filter(row => row.parentId === guideId);
    const originalGuideData = mainRows.find(item => item.ncg_name === guide.name);
    if (!originalGuideData) return;

    const guideData = {
      guide: { id: guideId, name: guide.name, originalData: originalGuideData },
      plans: plans.map(plan => {
        const originalPlanData = mainRows.find(item => item.ncg_name === guide.name && item.ncp_name === plan.name);
        return {
          id: plan.id,
          name: plan.name,
          originalData: originalPlanData,
          nursingInterventions: originalPlanData?.patientnursecareplandetails || [],
          sessions: originalPlanData?.patientnursecareplandetailsperiod || []
        };
      })
    };

    setCopiedGuideData(guideData);
  };

  const handlePasteGuide = async () => {
    if (!copiedGuideData) {
      enqueueSnackbar(t("patient-care.no-guide-copied"), { variant: 'warning' });
      return;
    }

    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    const selectedDate = new Date(date + "T00:00:00");
    if (selectedDate < todayDate) {
      enqueueSnackbar(t("patient-care.cannot-paste-past-date"), { variant: 'warning' });
      return;
    }

    try {
      const nursecareplanDetails = copiedGuideData.plans.flatMap((plan: any) =>
        plan.nursingInterventions.map((intervention: any) => ({
          nursecareplanid: plan.originalData?.ncp_id,
          nursecareplandetailid: intervention.ncpd_id,
          nursecareplandetailperiod: intervention.period,
          nursecareplanstartdate: date,
          nursecareplanenddate: date,
        }))
      );

      const requestData = {
        patientid: patientId,
        nursecareplan: nursecareplanDetails
      };

      const { nurseService } = await import("src/api/nurse/nurseService");
      await nurseService.patientNurseCarePlan.createPatientNurseCarePlan(requestData);

      enqueueSnackbar(t("patient-care.guide-pasted-successfully"), { variant: 'success' });
      setCopiedGuideData(null);
      setRefreshKey(prev => prev + 1);
    } catch (error: any) {
      console.error(error);
      enqueueSnackbar(t("patient-care.guide-paste-failed"), { variant: 'error' });
    }
  };

  const handleSaveEdit = (updatedRow: any) => {
    setMainRows(prev =>
      prev.map(row => row.id === updatedRow.id ? { ...row, ...updatedRow } : row)
    );
    setEditDialogOpen(false);
    setCurrentEditRow(null);
  };

  const handleSaveNurseEdit = (updatedRow: any) => {
    setSelectedChildDetails(prev =>
      prev.map(row => row.id === updatedRow.id ? { ...row, ...updatedRow } : row)
    );
    setNurseEditDialogOpen(false);
    setNurseCurrentEditRow(null);
  };

  const handleSavePlanEdit = (updatedRow: any) => {
    setSelectedPlanPeriods(prev =>
      prev.map(row => row.id === updatedRow.id ? { ...row, ...updatedRow } : row)
    );
    setPlanEditDialogOpen(false);
    setPlanCurrentEditRow(null);
  };

  const handleProcessAction = async (clickedId?: number) => {
    try {
      const idsToProcess: number[] = [];
      if (clickedId !== undefined) {
        idsToProcess.push(clickedId);
      }
      const uniqueIdsToProcess = Array.from(new Set(idsToProcess));
      setSelectedIds(uniqueIdsToProcess);
      return uniqueIdsToProcess;
    } catch (error) {
      console.error(error);
    }
  };

  return {
    date, patientId, setDate, handleDateChange,
    treeRows, isDataLoading, refreshKey, setRefreshKey,
    selectedChildDetails, setSelectedChildDetails,
    selectedPlanPeriods, setSelectedPlanPeriods,
    selectedIds, setSelectedIds,
    lastSelectedTreeId, setLastSelectedTreeId,
    lastSelectedInterventionId, setLastSelectedInterventionId,
    dialogOpen, setDialogOpen,
    isEditDialogOpen, setEditDialogOpen, currentEditRow, setCurrentEditRow,
    isNurseEditDialogOpen, setNurseEditDialogOpen, nurseCurrentEditRow, setNurseCurrentEditRow,
    isPlanEditDialogOpen, setPlanEditDialogOpen, planCurrentEditRow, setPlanCurrentEditRow,
    dialogTreeOpen, setDialogTreeOpen, dialogTreeNewOpen, setDialogTreeNewOpen,
    selectedTreeId, setSelectedTreeId,
    selectedRowForPeriodPlanning, setSelectedRowForPeriodPlanning,
    selectedRowDataForNewPeriod, setSelectedRowDataForNewPeriod,
    deleteState, setDeleteState,
    secondGridDeleteState, setSecondGridDeleteState,
    isDeleting, isSecondGridDeleting,
    handleDeleteGuide, handleDeletePlan, handleDeleteConfirm,
    handleDeleteIntervention, handleDeletePlanning, handleSecondGridDeleteConfirm,
    handleCopyGuide, handlePasteGuide, copiedGuideData,
    handleTreeSelect,
    buildTreeRows,
    mainRows,
    handleSaveEdit, handleSaveNurseEdit, handleSavePlanEdit,
    handleProcessAction
  };
};
